'use client'

import Link from 'next/link'
import { useMemo, useRef, useState, useEffect } from 'react'
import { format, startOfMonth } from 'date-fns'
import { Users, Shield } from 'lucide-react'
import type { Group, TripType, WeekWithStats } from '@/features/stm/lib/types'
import type { NonHomeownerUser, VolunteerTableRow } from '@/features/volunteers/types/volunteers.type'
import {
  extractArLeadershipEvents,
  countArLeadershipOnDay,
} from '@/features/volunteers/lib/ar-leadership-day-count'
import { MonthSpanCalendar } from '@/features/volunteers/components/month-span-calendar/MonthSpanCalendar'
import {
  buildCalendarWeeks,
  getWeekSpanSegments,
  compareGroupTripSegments,
} from '@/features/volunteers/components/month-span-calendar/month-span-calendar-utils'
import {
  groupToTripSpan,
  getTripParticipantCountForDay,
} from '@/features/volunteers/components/month-span-calendar/group-trip-calendar-utils'
import { cn } from '@/shared/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'

/** Trip type background/text (left edge = status: Confirmed / Tentative — see statusLeftBorderClass) */
const tripTypeBarStyles: Record<TripType, string> = {
  'Vision Trip': 'bg-blue-500/15 text-blue-800 dark:text-blue-300',
  'Skilled Labor': 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  Volunteer: 'bg-green-500/15 text-green-800 dark:text-green-300',
  'Youth Trip': 'bg-purple-500/15 text-purple-800 dark:text-purple-300',
  'Adult Trip': 'bg-indigo-500/15 text-indigo-800 dark:text-indigo-300',
  'Family Trip': 'bg-pink-500/15 text-pink-800 dark:text-pink-300',
  Semesters: 'bg-cyan-500/15 text-cyan-800 dark:text-cyan-300',
  Gap: 'bg-teal-500/15 text-teal-800 dark:text-teal-300',
  WR: 'bg-orange-500/15 text-orange-800 dark:text-orange-300',
  Leadership: 'bg-slate-500/15 text-slate-800 dark:text-slate-300',
  Partner: 'bg-violet-500/15 text-violet-800 dark:text-violet-300',
  Other: 'bg-gray-500/15 text-gray-800 dark:text-gray-300',
}

const BAR_BASE =
  'col-span-1 self-start rounded-md px-2 py-1 text-xs font-medium border-y border-r border-border/55 truncate text-left cursor-pointer hover:opacity-90 transition-opacity border-l-4'

/** Same language as WeekCard group rows: Confirmed solid green, Tentative dashed amber */
function statusLeftBorderClass(g: Group): string {
  if (g.status === 'Confirmed') return 'border-l-green-600 [border-left-style:solid]'
  if (g.status === 'Tentative') return 'border-l-amber-500 [border-left-style:dashed]'
  if (g.status === 'Cancelled') return 'border-l-slate-400 opacity-70 [border-left-style:solid]'
  return 'border-l-muted-foreground [border-left-style:solid]'
}

function groupBarLineLabel(g: Group): string {
  const raw = (g.group_name || 'Group').trim()
  const max = 26
  const name = raw.length > max ? `${raw.slice(0, max)}…` : raw
  return `${name} - E ${g.participants}/R ${g.registered_participants ?? 0}`
}

function findWeekForBarRow(weekStart: Date, weeks: WeekWithStats[] | undefined): WeekWithStats | null {
  if (!weeks?.length) return null
  const key = format(weekStart, 'yyyy-MM-dd')
  return weeks.find((w) => w.week_start.slice(0, 10) === key) ?? null
}

function MonthTripBarTooltipContent({
  g,
  weekForRow,
}: {
  g: Group
  weekForRow: WeekWithStats | null
}) {
  const span = groupToTripSpan(g)
  const range = span ? `${format(span.from, 'MMM d')} – ${format(span.to, 'MMM d')}` : null
  const ml = g.leader_name?.trim() || 'ML Needed'
  const notes = g.notes?.trim()

  return (
    <div className="space-y-2 text-left">
      <div>
        <p className="font-semibold text-sm text-foreground">{g.group_name}</p>
        {g.project_id?.trim() && (
          <p className="text-xs text-muted-foreground truncate">{g.project_id.trim()}</p>
        )}
      </div>
      <div className="space-y-1.5 text-xs text-foreground">
        {range && <p className="text-muted-foreground">{range}</p>}
        <p>
          <span className="font-medium">
            E {g.participants}/R {g.registered_participants ?? 0}
          </span>{' '}
          participants · ML: {ml}
        </p>
        <p className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
          <span
            className={cn(
              'font-semibold uppercase tracking-wide',
              g.status === 'Confirmed' && 'text-emerald-600 dark:text-emerald-400',
              g.status === 'Tentative' && 'text-amber-600 dark:text-amber-400',
              g.status === 'Cancelled' && 'text-slate-500'
            )}
          >
            {g.status}
          </span>
          {g.trip_type && (
            <>
              <span className="text-muted-foreground">·</span>
              <span>{g.trip_type}</span>
            </>
          )}
        </p>
      </div>
      {weekForRow && (
        <p className="border-t border-border/50 pt-2 text-xs text-muted-foreground">
          Week capacity:{' '}
          <span className="font-medium text-foreground">
            {weekForRow.filledSpots}/{weekForRow.capacity}
          </span>{' '}
          ({Math.round(weekForRow.fillRate)}% full · {weekForRow.availableSpots} open)
        </p>
      )}
      {notes && (
        <div className="border-t border-border/50 pt-2">
          <p className="mb-1 font-semibold text-primary">Group Notes</p>
          <p className="text-xs leading-relaxed whitespace-pre-wrap">{notes}</p>
        </div>
      )}
    </div>
  )
}

/** Same buckets as WeekCard getStatusColor — week capacity fill rate */
function weekCapacityTone(week: WeekWithStats): 'blocked' | 'over' | 'full' | 'warning' | 'available' {
  if (week.hasHardBlock) return 'blocked'
  if (week.fillRate > 100) return 'over'
  if (week.fillRate >= 90) return 'full'
  if (week.fillRate >= 70) return 'warning'
  return 'available'
}

const capacityMetricTextClass: Record<
  'blocked' | 'over' | 'full' | 'warning' | 'available',
  string
> = {
  available: 'text-emerald-600 dark:text-emerald-400',
  warning: 'text-amber-600 dark:text-amber-400',
  full: 'text-rose-600 dark:text-rose-400',
  over: 'text-rose-700 dark:text-rose-300',
  blocked: 'text-slate-500 dark:text-slate-400',
}

function findWeekForDay(day: Date, weeks: WeekWithStats[]): WeekWithStats | null {
  const d = format(day, 'yyyy-MM-dd')
  for (const w of weeks) {
    const ws = (w.week_start ?? '').slice(0, 10)
    const we = (w.week_end ?? '').slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ws) || !/^\d{4}-\d{2}-\d{2}$/.test(we)) continue
    if (d >= ws && d <= we) return w
  }
  return null
}

export interface GroupsMonthCalendarProps {
  groups: Group[]
  groupHref: (groupId: string) => string
  /** When true, show empty state inside the card */
  showEmptyState?: boolean
  /** Controlled month (syncs list data range with parent). */
  currentMonth: Date
  onCurrentMonthChange: (d: Date) => void
  /** When false, hide the inner month title/nav (parent already provides month controls). */
  showMonthToolbar?: boolean
  /** STM weeks with capacity/fillRate — used to color day-cell totals by week (green/yellow/red). */
  weeksWithCapacity?: WeekWithStats[]
  /** Primary/teal grid outline (ReliefHub brand) — default on for Calendar tab */
  brandOutline?: boolean
  /** AR Team users (serveDates) — when set with regionId, day cells show base-leader count like AR Team calendar */
  arUsers?: (NonHomeownerUser | VolunteerTableRow)[]
  regionId?: number | null
  /** Increment when parent "Today" should scroll the grid to the week row containing local today (toolbar hidden). */
  scrollToTodayTrigger?: number
}

export function GroupsMonthCalendar({
  groups,
  groupHref,
  showEmptyState = true,
  currentMonth,
  onCurrentMonthChange,
  showMonthToolbar = true,
  weeksWithCapacity,
  brandOutline = true,
  arUsers,
  regionId,
  scrollToTodayTrigger = 0,
}: GroupsMonthCalendarProps) {
  const [pendingScrollToToday, setPendingScrollToToday] = useState(false)
  const todayWeekRowRef = useRef<HTMLDivElement | null>(null)

  const weeks = useMemo(() => buildCalendarWeeks(currentMonth), [currentMonth])

  const arLeadershipEvents = useMemo(
    () =>
      arUsers && arUsers.length > 0 && regionId != null && regionId > 0
        ? extractArLeadershipEvents(arUsers, regionId)
        : [],
    [arUsers, regionId]
  )

  const spanEvents = useMemo(() => {
    const out: Array<Group & { from: Date; to: Date; sortKey: string }> = []
    for (const g of groups) {
      if (g.status === 'Cancelled') continue
      const r = groupToTripSpan(g)
      if (!r) continue
      const sortKey = (g.project_id?.trim() || g.group_name || g.id).toLowerCase()
      out.push({ ...g, from: r.from, to: r.to, sortKey })
    }
    return out
  }, [groups])

  const handlePrevMonth = () => {
    onCurrentMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    onCurrentMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const handleToday = () => {
    onCurrentMonthChange(startOfMonth(new Date()))
    setPendingScrollToToday(true)
  }

  useEffect(() => {
    if (scrollToTodayTrigger <= 0) return
    setPendingScrollToToday(true)
  }, [scrollToTodayTrigger])

  useEffect(() => {
    if (!pendingScrollToToday) return
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        todayWeekRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        setPendingScrollToToday(false)
      })
    })
    return () => cancelAnimationFrame(id)
  }, [pendingScrollToToday, currentMonth])

  const empty = spanEvents.length === 0

  return (
    <TooltipProvider delayDuration={300}>
    <div className="space-y-3">
      <MonthSpanCalendar
        currentMonth={currentMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        weeks={weeks}
        todayWeekRowRef={todayWeekRowRef}
        isLoading={false}
        showMonthToolbar={showMonthToolbar}
        brandOutline={brandOutline}
        getSegmentsForWeek={(weekStart) =>
          getWeekSpanSegments(spanEvents, weekStart, compareGroupTripSegments)
        }
        renderDayCell={({ day, dayCol, isCurrentMonth, isTodayDate }) => {
          const { participantSum } = getTripParticipantCountForDay(groups, day)
          const baseLeaderCount = countArLeadershipOnDay(arLeadershipEvents, day)
          const hasCount = participantSum > 0 || baseLeaderCount > 0
          const weekForDay = weeksWithCapacity?.length
            ? findWeekForDay(day, weeksWithCapacity)
            : null
          const capTone = weekForDay ? weekCapacityTone(weekForDay) : null
          const capClass = capTone ? capacityMetricTextClass[capTone] : 'text-muted-foreground'
          const weekCapacityTitle = weekForDay
            ? `Week ${weekForDay.week_start.slice(0, 10)}–${weekForDay.week_end.slice(0, 10)} · ${Math.round(weekForDay.fillRate)}% full (${weekForDay.filledSpots}/${weekForDay.capacity})`
            : undefined
          return (
            <div
              className={cn(
                'flex h-full min-h-[40px] w-full min-w-0 flex-row items-start justify-between gap-1 overflow-x-clip p-0.5 sm:min-h-[36px] sm:gap-1.5 sm:p-1 lg:items-center',
                dayCol < 6 &&
                  (brandOutline ? 'border-r border-primary/35' : 'border-r border-muted-foreground/40'),
                !isCurrentMonth && 'bg-muted/20',
                isTodayDate && 'bg-primary/5'
              )}
            >
              <span
                className={cn(
                  'inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full text-[11px] font-bold sm:h-7 sm:min-w-[1.75rem] sm:text-sm',
                  isCurrentMonth ? 'text-foreground' : 'text-muted-foreground',
                  isTodayDate && 'bg-primary text-primary-foreground'
                )}
              >
                {format(day, 'd')}
              </span>
              {hasCount && (
                <span
                  className={cn(
                    'flex min-w-0 flex-1 flex-col items-end gap-0.5 text-[9px] font-semibold leading-tight sm:text-[10px] md:text-xs lg:flex-row lg:items-center lg:justify-end lg:gap-1.5 lg:text-sm',
                    capClass
                  )}
                  title={weekCapacityTitle}
                >
                  <span
                    className="flex min-w-0 items-center justify-end gap-0.5"
                    title="Participants on trip (this day)"
                  >
                    <Users className="h-2.5 w-2.5 shrink-0 sm:h-3 sm:w-3 lg:h-3.5 lg:w-3.5" />
                    <span className="tabular-nums">{participantSum}</span>
                  </span>
                  <span
                    className="flex min-w-0 items-center justify-end gap-0.5"
                    title="Base leaders on base this day (AR Team schedule)"
                  >
                    <Shield className="h-2.5 w-2.5 shrink-0 text-teal-600 dark:text-teal-400 sm:h-3 sm:w-3 lg:h-3.5 lg:w-3.5" />
                    <span className="tabular-nums">{baseLeaderCount}</span>
                  </span>
                </span>
              )}
            </div>
          )
        }}
        renderWeekBarRow={(weekStart, _weekIdx, weekSegments) => {
          const weekForRow = findWeekForBarRow(weekStart, weeksWithCapacity)
          return (
            <>
              {weekSegments.map((seg) => {
                const g = seg.event
                const colStart = seg.startCol + 1
                const colEnd = seg.endCol + 2
                const tripClass = g.trip_type
                  ? tripTypeBarStyles[g.trip_type]
                  : 'bg-muted/50 text-muted-foreground'
                return (
                  <Tooltip key={`${g.id}-${weekStart.toISOString()}-${seg.startCol}`}>
                    <TooltipTrigger asChild>
                      <Link
                        href={groupHref(g.id)}
                        className={cn(BAR_BASE, tripClass, statusLeftBorderClass(g))}
                        style={{ gridColumn: `${colStart} / ${colEnd}` }}
                      >
                        {groupBarLineLabel(g)}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="start"
                      sideOffset={6}
                      collisionPadding={16}
                      className="z-[100] max-h-[min(70vh,22rem)] max-w-[min(100vw-1.5rem,320px)] overflow-y-auto overflow-x-hidden border border-border bg-white px-3 py-2.5 text-foreground shadow-md dark:bg-slate-800"
                    >
                      <MonthTripBarTooltipContent g={g} weekForRow={weekForRow} />
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </>
          )
        }}
      />

      {empty && showEmptyState && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-muted-foreground">
          <p className="text-sm font-medium">No trips with dates in this filtered set</p>
          <p className="text-xs mt-1">Adjust search or filters, or pick another month.</p>
        </div>
      )}
    </div>
    </TooltipProvider>
  )
}
